import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import CustomSnackbar from "../components/Message.Component";
import { useGetGlobalProductInformation, useSalesByCategoryMetrics, useAveragePriceMetrics, useSalesByPriceRangeMetrics, useSalesByBrandMetrics, useCancellationRateMetrics, useProductLifecycleMetrics, usePriceVolumeCorrelationMetrics, useCatalogAgeMetrics, useSkuPerformanceComparison, useSalesByRegionMetrics, useOrderCompletionTimeMetrics } from "../../client/hooks/products/products.queries";

// Componentes
import MetricsHeader from "./components/metrics/MetricsHeader";
import LoadingMetrics from "./components/metrics/LoadingMetrics";
import VisaoGeralTab from "./components/metrics/tabs/VisaoGeralTab";
import VendasDesempenhoTab from "./components/metrics/tabs/VendasDesempenhoTab";
import PrecosReceitaTab from "./components/metrics/tabs/PrecosReceitaTab";
import PedidosEntregasTab from "./components/metrics/tabs/PedidosEntregasTab";
import CategoriasMarcasTab from "./components/metrics/tabs/CategoriasMarcasTab";
import GeografiaVendasTab from "./components/metrics/tabs/GeografiaVendasTab";

// Utilitários
import { periodoToDates } from "./components/metrics/utils/formatters";

export default function MetricasPage() {
  // Estados
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");
  const [periodoSelecionado, setPeriodoSelecionado] = useState("30dias");
  const [activeTab, setActiveTab] = useState(0);
  
  // Lista de canais disponíveis
  const canais = [
    { label: "Mercado Livre", value: "MERCADO_LIVRE" },
    { label: "Shopee", value: "SHOPEE" },
    { label: "Amazon", value: "AMAZON" },
    { label: "Magalu", value: "MAGALU" },
  ];
  
  const [canalSelecionado, setCanalSelecionado] = useState("MERCADO_LIVRE");
  // Estado das métricas
  const [metricas, setMetricas] = useState({
    totalProdutos: 0,
    produtosAtivos: 0,
    produtosInativos: 0,
    produtosEmEstoque: 0,
    produtosEmFalta: 0,
    percentAtivos: 0,
    percentInativos: 0,
    percentEmEstoque: 0,
    percentEmFalta: 0,
    estoqueTotal: 0,
    valorCatalogo: 0,
    moeda: 'BRL',
    ultimaSincronizacao: '-',
    produtosMaisVendidos: [],
    produtosMenosVendidos: [],
    categoriasMaisVendidas: [],
  });
  // Parâmetros de data baseados no período selecionado
  const dateParams = periodoToDates[periodoSelecionado]();
  
    // Consultas de API para buscar dados
  const { data: globalInfo, isLoading: loadingGlobal } = useGetGlobalProductInformation(canalSelecionado, true);
  // Estado para o SKU que será usado para buscar dados específicos
  const [currentSkuId, setCurrentSkuId] = useState(undefined);
  const [loadingSkuData, setLoadingSkuData] = useState(false);
    // Usar o primeiro SKU disponível como default se não tiver um específico selecionado
  const defaultSkuId = globalInfo && globalInfo.skus && globalInfo.skus.length > 0 ? globalInfo.skus[0].sellerSku : undefined;
  const skuId = currentSkuId || defaultSkuId;
  
  const partnerId = globalInfo && globalInfo.products && globalInfo.products.length > 0 ? globalInfo.products[0].partnerId : undefined;
  
  // Limpar o SKU atual quando o canal é alterado
  useEffect(() => {
    if (currentSkuId) {
      setCurrentSkuId(undefined);
      setAlert(true);
      setMessage(`Canal alterado para ${canais.find(c => c.value === canalSelecionado)?.label || canalSelecionado}. Filtros de SKU resetados.`);
      setSeverity("info");
    }
  }, [canalSelecionado]);
  
  // Função para atualizar o SKU atual - será passada para o componente VisaoGeralTab
  const handleUpdateSku = (newSkuId) => {
    setLoadingSkuData(true);
    setCurrentSkuId(newSkuId);
    
    // Se estamos limpando o filtro
    if (!newSkuId) {
      setAlert(true);
      setMessage("Filtro limpo. Exibindo dados do produto padrão.");
      setSeverity("info");
      // Desativa o estado de loading mais rapidamente em caso de limpar o filtro
      setTimeout(() => setLoadingSkuData(false), 500);
    } else {
      // Para pesquisa de SKU, mostramos uma notificação indicando que a busca foi iniciada
      setAlert(true);
      setMessage(`Buscando dados para o SKU "${newSkuId}"...`);
      setSeverity("info");
    }
  };

    const { data: productLifecycle, isLoading: loadingLifecycle } = useProductLifecycleMetrics(skuId, { months: 6, channel: canalSelecionado }, !!skuId);
  
  // Exibe notificação quando os dados do ciclo de vida forem carregados
  useEffect(() => {
    if (!loadingLifecycle && loadingSkuData && currentSkuId) {
      if (productLifecycle) {
        setAlert(true);
        
        if (productLifecycle.product && productLifecycle.product.skuTitle) {
          setMessage(`Dados do SKU "${productLifecycle.product.skuTitle}" carregados com sucesso.`);
          setSeverity("success");
        } else {
          setMessage(`SKU não encontrado ou sem dados históricos.`);
          setSeverity("warning");
        }
      }
    }
  }, [loadingLifecycle, loadingSkuData, productLifecycle, currentSkuId]);

  const { data: salesByCategory, isLoading: loadingCategory } = useSalesByCategoryMetrics({ ...dateParams, limit: 5, channel: canalSelecionado }, true);
  const { data: averagePrice, isLoading: loadingAvgPrice } = useAveragePriceMetrics({ ...dateParams, channel: canalSelecionado }, true);
  const { data: salesByPriceRange, isLoading: loadingPriceRange } = useSalesByPriceRangeMetrics({ ...dateParams, channel: canalSelecionado }, true);
  const { data: salesByBrand, isLoading: loadingBrand } = useSalesByBrandMetrics({ ...dateParams, channel: canalSelecionado, limit: 5 }, true);
  const { data: cancellationRate, isLoading: loadingCancel } = useCancellationRateMetrics({ ...dateParams, channel: canalSelecionado }, true);
  const { data: priceVolumeCorrelation, isLoading: loadingCorrelation } = usePriceVolumeCorrelationMetrics({ ...dateParams, channel: canalSelecionado }, true);
  const { data: catalogAge, isLoading: loadingCatalogAge } = useCatalogAgeMetrics({ ...dateParams, channel: canalSelecionado }, true);
  const { data: salesByRegion, isLoading: loadingRegion } = useSalesByRegionMetrics({ ...dateParams, channel: canalSelecionado }, true);
  const { data: orderCompletionTime, isLoading: loadingOrderTime } = useOrderCompletionTimeMetrics({ ...dateParams, channel: canalSelecionado }, true);
  const { data: skuPerformance } = useSkuPerformanceComparison(partnerId, { ...dateParams, channel: canalSelecionado }, !!partnerId);
  // Atualiza métricas globais quando os dados chegam
  useEffect(() => {
    if (globalInfo) {
      setMetricas((prev) => ({
        ...prev,
        totalProdutos: globalInfo.totalProducts || 0,
        produtosAtivos: globalInfo.activeProducts || 0,
        produtosInativos: globalInfo.inactiveProducts || 0,
        produtosEmEstoque: globalInfo.productsInStock || 0,
        produtosEmFalta: globalInfo.productsOutOfStock || 0,
        percentAtivos: globalInfo.activeProductsPercent || 0,
        percentInativos: globalInfo.inactiveProductsPercent || 0,
        percentEmEstoque: globalInfo.productsInStockPercent || 0,
        percentEmFalta: globalInfo.productsOutOfStockPercent || 0,
        estoqueTotal: globalInfo.totalStock || 0,
        valorCatalogo: globalInfo.catalogValue || 0,
        moeda: globalInfo.currency || 'BRL',
        ultimaSincronizacao: globalInfo.lastSync || '-',
      }));
    }
  }, [globalInfo]);

  // Atualiza categorias mais vendidas quando os dados chegam
  useEffect(() => {
    if (salesByCategory) {
      setMetricas((prev) => ({
        ...prev,
        categoriasMaisVendidas: salesByCategory.map((cat) => ({
          nome: cat.categoryName,
          totalVendas: cat.totalSales,
          receita: cat.totalRevenue,
        })),
      }));
    }
  }, [salesByCategory]);
  // Atualiza o estado de carregamento
  useEffect(() => {
    setLoading(
      loadingGlobal || 
      loadingCategory || 
      loadingAvgPrice || 
      loadingPriceRange || 
      loadingBrand || 
      loadingCancel || 
      loadingCorrelation || 
      loadingCatalogAge || 
      loadingRegion || 
      loadingOrderTime ||
      loadingLifecycle
    );
    
    // Desativa o indicador de carregamento específico do SKU quando terminar
    if (!loadingLifecycle && loadingSkuData) {
      setLoadingSkuData(false);
    }
  }, [
    loadingGlobal, 
    loadingCategory, 
    loadingAvgPrice, 
    loadingPriceRange, 
    loadingBrand, 
    loadingCancel, 
    loadingCorrelation, 
    loadingCatalogAge, 
    loadingRegion, 
    loadingOrderTime,
    loadingLifecycle,
    loadingSkuData, 
    canalSelecionado
  ]);
  // Handlers
  const handlePeriodoChange = (event) => {
    setPeriodoSelecionado(event.target.value);
    // Não força refresh automaticamente - o usuário precisa clicar em Filtrar
  };
      const handleCanalChange = (event) => {
    const newChannel = event.target.value;
    setCanalSelecionado(newChannel);
    
    // Feedback ao usuário
    setLoading(true);
    setAlert(true);
    setMessage(`Carregando dados do canal ${canais.find(c => c.value === newChannel)?.label || newChannel}...`);
    setSeverity("info");
    // Nota: Mudar o canal já é suficiente para disparar o refetch das queries
    // já que o canal é incluído como dependência nas queryKeys
  };

  // Função para forçar a atualização dos dados
  const handleRefreshData = () => {
    setLoading(true);
    setAlert(true);
    setMessage(`Atualizando métricas para ${canais.find(c => c.value === canalSelecionado)?.label || canalSelecionado}...`);
    setSeverity("info");
    
    // Para garantir que ocorra um refetch completo de todas as queries, 
    // precisamos alterar as queryKeys que incluem o período e canal
    
    // Armazena o período atual
    const currentPeriodo = periodoSelecionado;
    
    // Primeiro mudamos o período para um valor temporário
    setPeriodoSelecionado('temp_refresh');
    
    // E então restauramos para o valor original após um breve delay
    // Isso força o React Query a reconhecer a mudança nas dependências da query
    setTimeout(() => {
      setPeriodoSelecionado(currentPeriodo);
    }, 100);
  };

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
  };
  // Renderização condicional das abas
  const renderTabContent = () => {
    switch(activeTab) {      case 0:
        return (
          <VisaoGeralTab
            metricas={metricas}
            averagePrice={averagePrice}
            cancellationRate={cancellationRate}
            catalogAge={catalogAge}
            productLifecycle={productLifecycle}
            onUpdateSku={handleUpdateSku}
            loadingSkuData={loadingSkuData}
            loadingMetrics={loadingGlobal}
          />
        );
      case 1:
        return (
          <VendasDesempenhoTab
            salesByCategory={salesByCategory}
            salesByBrand={salesByBrand}
            salesByRegion={salesByRegion}
            skuPerformance={skuPerformance}
          />
        );
      case 2:
        return (
          <PrecosReceitaTab
            averagePrice={averagePrice}
            salesByPriceRange={salesByPriceRange}
            priceVolumeCorrelation={priceVolumeCorrelation}
          />
        );
      case 3:
        return (
          <PedidosEntregasTab
            orderCompletionTime={orderCompletionTime}
            cancellationRate={cancellationRate}
            salesByRegion={salesByRegion}
          />
        );
      case 4:
        return (
          <CategoriasMarcasTab
            salesByCategory={salesByCategory}
            salesByBrand={salesByBrand}
            catalogAge={catalogAge}
          />
        );
      case 5:
        return (
          <GeografiaVendasTab
            salesByRegion={salesByRegion}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <CustomSnackbar
        open={alert}
        onClose={() => setAlert(false)}
        severity={severity}
        message={message}
      />      <Box sx={{ p: { xs: 1, md: 3 }, background: "#f7f9fb", minHeight: '100vh' }}>
        {/* Cabeçalho integrado com navegação */}
        <MetricsHeader
          periodoSelecionado={periodoSelecionado}
          handlePeriodoChange={handlePeriodoChange}
          canalSelecionado={canalSelecionado}
          handleCanalChange={handleCanalChange}
          canais={canais}
          lastSync={globalInfo?.lastSync}
          onRefresh={handleRefreshData}
          isLoading={loading}
          activeTab={activeTab}
          handleTabChange={handleTabChange}
        />
        
        {loading ? (
          <LoadingMetrics />
        ) : (
          <Box sx={{ mt: 3 }}>
            {renderTabContent()}
          </Box>
        )}
      </Box>
    </>
  );
}
